import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Login() {
    const { user, setUser, loading } = useAuth();
    const navigate = useNavigate();
    const [signingIn, setSigningIn] = useState(false);
    const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

    const handleAuthSuccess = useCallback(async (payload) => {
        try {
            setSigningIn(true);
            const res = await api.post("/auth/google", payload);
            if (res.data?.session_token) {
                localStorage.setItem("session_token", res.data.session_token);
            }
            setUser(res.data.user);
            if (!res.data.user?.onboarded) {
                navigate("/onboarding", { replace: true, state: { user: res.data.user } });
            } else if (res.data.user?.role === "resident" && res.data.user?.approval_status === "pending" && !res.data.user?.is_owner) {
                navigate("/pending-approval", { replace: true });
            } else {
                navigate("/dashboard", { replace: true, state: { user: res.data.user } });
            }
        } catch (err) {
            console.error("Google sign-in error:", err);
            toast.error(err.response?.data?.detail || "Google sign-in failed. Please try again.");
        } finally {
            setSigningIn(false);
        }
    }, [navigate, setUser]);

    useEffect(() => {
        if (!googleClientId) return;

        const setupGis = () => {
            if (window.google?.accounts?.id) {
                try {
                    window.google.accounts.id.initialize({
                        client_id: googleClientId,
                        callback: (response) => {
                            if (response?.credential) {
                                handleAuthSuccess({ credential: response.credential });
                            }
                        },
                        auto_select: false,
                        cancel_on_tap_outside: true,
                    });
                } catch (e) {
                    console.warn("Failed to initialize Google One Tap:", e);
                }
            }
        };

        if (window.google?.accounts?.id) {
            setupGis();
        } else {
            const timer = setInterval(() => {
                if (window.google?.accounts?.id) {
                    clearInterval(timer);
                    setupGis();
                }
            }, 300);
            return () => clearInterval(timer);
        }
    }, [googleClientId, handleAuthSuccess]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-nest-base">
                <div className="h-10 w-10 rounded-full border-2 border-nest-clay border-t-transparent animate-spin"></div>
            </div>
        );
    }
    if (user) {
        if (!user.onboarded) return <Navigate to="/onboarding" replace />;
        if (user.role === "resident" && user.approval_status === "pending" && !user.is_owner) {
            return <Navigate to="/pending-approval" replace />;
        }
        return <Navigate to="/dashboard" replace />;
    }

    const handleGoogleLogin = () => {
        // 1. If Google OAuth Client ID is configured and GIS oauth2 is loaded, trigger standard Google popup
        if (googleClientId && window.google?.accounts?.oauth2) {
            try {
                const tokenClient = window.google.accounts.oauth2.initTokenClient({
                    client_id: googleClientId,
                    scope: "email profile openid",
                    callback: async (tokenResponse) => {
                        if (tokenResponse?.access_token) {
                            await handleAuthSuccess({ access_token: tokenResponse.access_token });
                        } else if (tokenResponse?.error) {
                            console.error("Google OAuth token error:", tokenResponse.error);
                            toast.error(`Google Sign-In: ${tokenResponse.error_description || tokenResponse.error}`);
                        }
                    },
                    error_callback: (err) => {
                        console.error("Google token client error:", err);
                        if (err?.type !== "popup_closed") {
                            toast.error("Google sign-in popup error. Please check Authorized JavaScript origins.");
                        }
                    },
                });
                tokenClient.requestAccessToken({ prompt: "select_account" });
                return;
            } catch (e) {
                console.warn("initTokenClient failed, trying fallback:", e);
            }
        }

        // 2. If GIS ID is loaded, try prompt
        if (googleClientId && window.google?.accounts?.id) {
            window.google.accounts.id.prompt();
            return;
        }

        // 3. Fallback to Emergent OAuth
        const redirectUrl = window.location.origin + "/dashboard";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <div className="min-h-screen relative overflow-hidden bg-nest-base">
            <div className="absolute inset-0">
                <img
                    src="https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?crop=entropy&cs=srgb&fm=webp&w=1280&q=75&fit=crop"
                    alt="Residential"
                    className="absolute inset-0 w-full h-full object-cover"
                    decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/40 to-black/70"></div>
            </div>

            <div className="relative z-10 grid lg:grid-cols-2 min-h-screen">
                {/* Left brand */}
                <div className="flex flex-col justify-between p-5 sm:p-10 lg:p-16 text-white">
                    <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-md bg-white text-nest-ink grid place-items-center font-display font-semibold">N</div>
                        <span className="font-display text-xl tracking-tight">NeighborNest</span>
                    </div>
                    <div className="hidden lg:block max-w-md">
                        <p className="nest-stat-label text-white/70">Society Operating System</p>
                        <h1 className="font-display text-5xl xl:text-6xl font-medium leading-[1.05] mt-4 tracking-tighter">
                            Where every gate, bill and neighbour <span className="italic font-normal text-nest-sand">finds its place.</span>
                        </h1>
                        <p className="mt-6 text-white/70 leading-relaxed">
                            Pre-approve guests with QR. Track maintenance. Resolve complaints. Built for residents, admins and guards working in harmony.
                        </p>
                    </div>
                    <div className="hidden sm:block text-xs text-white/60 tracking-widest uppercase">Est. 2026 · Resident-first</div>
                </div>

                {/* Right login card */}
                <div className="flex items-center justify-center p-4 sm:p-6 lg:p-16">
                    <div className="w-full max-w-md bg-white/95 md:backdrop-blur-xl md:backdrop-saturate-150 border border-white/40 rounded-2xl shadow-xl p-6 sm:p-8 lg:p-10 transform-gpu" data-testid="login-card">
                        <p className="nest-stat-label">Welcome</p>
                        <h2 className="font-display text-3xl lg:text-4xl mt-2 tracking-tighter text-nest-ink">Step inside your nest.</h2>
                        <p className="mt-3 text-nest-ink2 text-sm">Sign in with Google to access your dashboard. Admins, residents and guards land in views tailored just for them.</p>

                        <button
                            onClick={handleGoogleLogin}
                            disabled={signingIn}
                            data-testid="google-login-btn"
                            className="mt-8 w-full flex items-center justify-center gap-3 bg-nest-ink text-white py-3.5 rounded-xl hover:bg-nest-clay transition-colors group disabled:opacity-50 shadow-md"
                        >
                            {signingIn ? (
                                <div className="h-5 w-5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                            ) : (
                                <>
                                    <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.2 35.5 24 35.5c-6.4 0-11.5-5.1-11.5-11.5S17.6 12.5 24 12.5c2.9 0 5.6 1.1 7.7 2.9l5.7-5.7C33.9 6.4 29.2 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5c10.2 0 19-7.6 19-19.5 0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 19 13 24 13c2.9 0 5.6 1.1 7.7 2.9l5.7-5.7C33.9 6.9 29.2 5 24 5 16.3 5 9.6 9.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 43c5.1 0 9.7-1.9 13.2-5.1l-6.1-5.2C29 34.4 26.6 35 24 35c-5.2 0-9.6-3.3-11.2-7.9l-6.5 5C9.5 38.7 16.2 43 24 43z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.2 5.7l6.1 5.2C40 35.3 43.5 30.1 43.5 24c0-1.2-.1-2.3-.4-3.5z"/></svg>
                                    <span className="font-medium tracking-tight">Continue with Google</span>
                                </>
                            )}
                        </button>

                        <div className="mt-8 grid grid-cols-3 gap-3 text-center">
                            <div className="border border-nest-line rounded-lg p-3">
                                <p className="text-[10px] font-bold uppercase tracking-widest text-nest-ink3">Admin</p>
                                <p className="text-xs mt-1 text-nest-ink2">Run the society</p>
                            </div>
                            <div className="border border-nest-line rounded-lg p-3">
                                <p className="text-[10px] font-bold uppercase tracking-widest text-nest-ink3">Resident</p>
                                <p className="text-xs mt-1 text-nest-ink2">Pay & invite</p>
                            </div>
                            <div className="border border-nest-line rounded-lg p-3">
                                <p className="text-[10px] font-bold uppercase tracking-widest text-nest-ink3">Guard</p>
                                <p className="text-xs mt-1 text-nest-ink2">Scan & secure</p>
                            </div>
                        </div>
                        <p className="mt-6 text-[11px] text-nest-ink3 leading-relaxed">First account becomes the society admin. Subsequent users choose Resident or Guard after sign-in.</p>
                    </div>
                </div>
            </div>
        </div>
    );
}
